import cookie from 'js-cookie'
import config from 'app-config'


declare global {
  interface Window {
    testAnalytics: {
      dispatch(name: string, methodName: string, args: any[]): void
    }
  }
}


const isLocal           = Boolean(process.env.LOCALTEST || process.env.WEBPACK === 'dev')
const isProdEnv         = config.runEnv === 'prod'
const areEventsDisabled = isLocal || Boolean(cookie.getJSON('noAnalytics'))
const isLogEnabled      = Boolean(cookie.getJSON('logAnalytics'))

if (typeof window !== 'undefined') {
  window.__debug__ = {
    ...window.__debug__,
    isLocal,
    isProdEnv,
    areEventsDisabled,
    resolvedAnalytics: [],
  }
}


const logToSentry = (err: Error): void => {
  if (
    typeof window !== 'undefined'
    && typeof window.Sentry === 'object'
    && typeof window.Sentry.captureException === 'function'
  ) {
    console.error(err)
    window.Sentry.captureException(err)
  }
}


interface ConsoleLogger {
  (entityName: string, methodName: string, ...args: string[]): void
}

const logToConsole: ConsoleLogger = (entityName, methodName, ...args) => {
  if (!process.env.UNIT_TESTS) {
    const logs = [
      `%c[analytics] %c${entityName}${methodName ? `.${methodName}` : ''}()`,
      'color: #4D9CFF;',
      'font-weight: bold; color: #0000FF;',
      ...(args || []),
    ]

    console.log(...logs)
  }
}


interface WindowResolverMethods {
  [method: string]: boolean | WindowResolverMethods
}

interface WindowResolverOpts {
  name: string
  methods: WindowResolverMethods
  resolver(): boolean
  eventHandler(methodName: string, ...args: any[]): void
  modifyTestAnalyticsArgs?(methodName: string, ...args: any[]): any[]
  logToConsole?: ConsoleLogger | false
  validate?(eventName: string, ...args: any[]): boolean
  timeout?: number
}


class WindowResolver {

  private initialized: boolean
  private waitList: any[]
  private opts: WindowResolverOpts

  private defaultOpts = {
    logToConsole,
  }

  constructor(opts: WindowResolverOpts) {
    this.opts         = { ...this.defaultOpts, ...opts }
    this.initialized  = false
    this.waitList     = []

    this.checkOpts()
    this.initResolving()

    const entity = this.setupMethods(this.opts.methods)

    // TODO replace with process.DEV
    if (typeof window !== 'undefined') {
      window[`_${this.opts.name}`] = entity
      window.__debug__.resolvedAnalytics.push(this.opts.name)
    }

    return entity
  }

  private checkOpts(): void {
    [ 'name', 'methods', 'resolver', 'eventHandler' ].forEach((key) => {
      if (!this.opts[key]) {
        throw new Error(`"${key}" required in WindowResolver options`)
      }
    })
  }

  private initResolving(): void {
    if (typeof window === 'undefined') {
      return
    }

    if (isLocal || this.opts.resolver()) {
      this.initialized = true
    }
    else {
      let interval: NodeJS.Timeout
      let timeout: NodeJS.Timeout

      interval = setInterval(() => {
        if (this.opts.resolver()) {
          this.initialized = true

          clearInterval(interval)
          clearInterval(timeout)
          this.resolveWaitList()
        }
      }, 500)

      // stop attempting to resolve entity
      setTimeout(() => {
        clearInterval(interval)
      }, this.opts.timeout || 15000)
    }
  }

  private resolveWaitList(): void {
    this.waitList.forEach((method) => method())
    this.waitList = []
  }

  private setupMethods(methods: WindowResolverMethods, methodName: string = '') {
    return Object.keys(methods).reduce((result, key) => {
      const value = methods[key]

      if (!value) {
        return result
      }

      const _methodName = !methodName ? key : `${methodName}.${key}`

      if (typeof value === 'object') {
        return {
          ...result,
          [key]: this.setupMethods(value, _methodName),
        }
      }

      if (typeof value === 'boolean') {
        return {
          ...result,
          [key]: (...args: any) => this.resolveCall(_methodName, ...args),
        }
      }

      return null
    }, {})
  }

  private resolveCall(methodName: string, ...args: any[]) {
    if (process.env.WEBPACK === 'build.server') {
      return
    }

    const method = () => {
      if (this.opts.logToConsole && (!isProdEnv || isLogEnabled)) {
        this.opts.logToConsole(this.opts.name, methodName, ...args)
      }

      // window.testAnalytics described in SSR - this functionality required for QA to test events
      if (window.testAnalytics) {
        try {
          if (typeof this.opts.modifyTestAnalyticsArgs === 'function') {
            const modifiedArgs = this.opts.modifyTestAnalyticsArgs(methodName, ...args)

            window.testAnalytics.dispatch(this.opts.name, methodName, modifiedArgs)
          }
          else {
            window.testAnalytics.dispatch(this.opts.name, methodName, args)
          }
        }
        catch (err) {
          logToSentry(err)
        }
      }

      let isValid = true

      if (this.opts.validate) {
        isValid = this.opts.validate(methodName, ...args)
      }

      if (isValid && !areEventsDisabled) {
        try {
          this.opts.eventHandler(methodName, ...args)
        }
        catch (err) {
          logToSentry(err)
        }
      }
    }

    if (this.initialized) {
      method()
    }
    else {
      this.waitList.push(method)
    }
  }
}


export default WindowResolver

export {
  logToConsole,
}
